/**
 * ══ QUYỀN THEO VAI TRÊN SỔ ĐĂNG KÝ GIAO DIỆN (D03c §3.3 · §4 · §8) ═══════════════════════════════════════
 * Dữ liệu thuần + hàm thuần như `routes.ts` — web và máy chủ cùng import, KHÔNG Cedar, KHÔNG `node:fs`.
 *
 * HAI LỚP, LỚP SAU ĐÈ LỚP TRƯỚC:
 *   1. MẶC ĐỊNH (`defaultGrant`) — đúng hiện trạng trước khi có màn cấu hình: Xem màn theo `RESTRICTED_ROUTES`, cộng
 *      các tab đang ẩn theo vai. CSDL trống ⇒ quyền y hệt hôm nay. `grants.test.ts` so lớp này với bảng kỳ vọng GÕ TAY.
 *   2. PHẦN CHỈNH (`role_grant` trong CSDL, nạp bằng `setGrantOverrides`) — chỉ những ô anh đổi khác mặc định.
 *
 * KẾ THỪA: ô không khai ⇒ "như cha". Con KHÔNG BAO GIỜ vượt cha; Sửa/Thêm/Xuất đòi Xem ở cùng nút.
 * KHOÁ CỨNG (§4.1): admin luôn đủ · `ncc` chỉ cổng thầu phụ · 7 vai nội bộ không vào cổng thầu phụ — phần chỉnh không
 * đè được những ô này.
 *
 * ⚠ Bảng phần chỉnh là TRẠNG THÁI CỦA TIẾN TRÌNH (một bảng cho cả tiến trình, kèm số bản). Máy chủ nạp lại mỗi khi số bản
 *   trong CSDL đổi (`plugins/authorize.ts`); web nạp theo phiên (`/api/v1/phan-quyen/hieu-luc`). Chọn cách này thay vì kéo
 *   bảng qua tham số của ~150 hàm `may*` ở các module: các hàm đó đã gọi `canAccessRoute`, nên chỉ cần `canAccessRoute`
 *   đọc bảng này là cả cây đi theo (KIEN-TRUC §2.7 — đơn giản đủ để một người trực đêm sửa được).
 */
import { PERMS, type Perm } from './ui-tree/types.ts'
import { UI_TREE, uiAncestors, uiNode } from './ui-tree.ts'
import {
  AP_DOC_READ_ROLES, AZ_OFFICE, COMMISSION_READ_ROLES, PAY_REQUEST_OWN_ONLY_ROLES, PUBLIC_ROUTES, RESTRICTED_ROUTES, STAFF_ROLES,
  canSeeVendorCost,
  type CostViewFlags,
} from './routes.ts'

export { PERMS, type Perm }

/** Gốc của cổng thầu phụ trong sổ. */
const VENDOR_ROOT = 'vendorPortal'

const keyOf = (role: string, node: string, perm: Perm): string => `${role}|${node}|${perm}`

export interface GrantRow { role: string; node: string; perm: Perm; allow: boolean }

const inVendorTree = (id: string): boolean => id === VENDOR_ROOT || uiAncestors(id).some((a) => a.id === VENDOR_ROOT)

/**
 * ══ "QUYỀN VỀ QUYỀN" — CỐ ĐỊNH ADMIN · GĐ (§4.2, anh chốt 29/10) ══
 * Ai sửa được những ô này là tự nới được quyền của mình (tự cho mình duyệt chi, tự mở màn cho mình) ⇒ không đưa lên màn
 * cấu hình. Khoá theo `nút` (mọi quyền) hoặc `nút|quyền`.
 */
const FIXED: Readonly<Record<string, readonly string[]>> = {
  // 🔐 Phân quyền — chính màn cấu hình.
  'catalog.permissions': ['admin', 'giam_doc'],
  // ✅ Cấp duyệt — sửa (`approvalRule:write`): `extraUserIds` cho qua MỌI bước kể cả "đã chi".
  'catalog.approvals|edit': ['admin', 'giam_doc'],
  'catalog.approvals.byStep|edit': ['admin', 'giam_doc'],
  'catalog.approvals.byUser|edit': ['admin', 'giam_doc'],
}
const fixedRoles = (node: string, perm: Perm): readonly string[] | undefined => FIXED[node] ?? FIXED[`${node}|${perm}`]

/**
 * Ô khoá cứng (§4.1 · §4.2): trả lý do, hoặc `null` nếu ô sửa được trên màn cấu hình.
 * Màn cấu hình hiện 🔒 kèm lý do; `can()` bỏ qua phần chỉnh ở những ô này.
 */
export const lockedReason = (role: string, node: string, perm: Perm = 'view'): string | null => {
  if (role === 'admin') return 'Quản trị luôn có đủ mọi quyền.'
  const vendor = inVendorTree(node)
  if (role === 'ncc' && !vendor) return 'Thầu phụ chỉ dùng cổng thầu phụ.'
  if (role !== 'ncc' && vendor) return 'Cổng thầu phụ chỉ dành cho tài khoản thầu phụ.'
  if (fixedRoles(node, perm) !== undefined) return 'Quyền về quyền — cố định Quản trị · Giám đốc (ai sửa được là tự nới được quyền của mình).'
  return null
}

/**
 * ══ MẶC ĐỊNH = HIỆN TRẠNG ══
 * `undefined` = không khai (như cha). Chỉ khai ở gốc (màn) và ở những tab hôm nay đang ẩn theo VAI. Tab ẩn theo CỜ TỪNG
 * NGƯỜI (giá vốn) không khai ở đây — đó là §4.4, màn tự xét cờ.
 */
const TAB_DEFAULT_VIEW: Readonly<Record<string, readonly string[]>> = {
  // 🧾 Hóa đơn MH — `ap.tsx` `canApDoc` (`AP_DOC_READ_ROLES`, bản cũ `IS_APDOC`).
  'purchaseDocs.invoices': AP_DOC_READ_ROLES,
  // 💸 Phát hoa hồng — `commission.tsx` `canPhat`: vai đọc hoa hồng TRỪ trưởng phòng KD.
  'commission.phat': COMMISSION_READ_ROLES.filter((r) => r !== 'truong_phong_kd'),
}

const screenDefaultView = (role: string, path: string): boolean => {
  const rule = RESTRICTED_ROUTES.find((r) => r.path === path)
  if (rule === undefined) return (STAFF_ROLES as readonly string[]).includes(role)
  // Màn giá vốn: vai ngoài `roles` vẫn được Xem theo VAI, rồi `canAccessRoute` đòi thêm cờ giá vốn của NGƯỜI (§4.4).
  if (rule.costViewEscalation === true) return (STAFF_ROLES as readonly string[]).includes(role)
  return rule.roles.includes(role)
}

export const defaultGrant = (role: string, node: string, perm: Perm): boolean | undefined => {
  const n = uiNode(node)
  if (n === undefined) return undefined
  // Mọi MÀN (kể cả bốn màn con của Kế toán) có mặc định theo luật đường dẫn của chính nó, không "như cha".
  if (n.kind === 'screen' && n.path !== undefined) {
    if (n.id === VENDOR_ROOT) return role === 'ncc'
    return screenDefaultView(role, n.path)
  }
  if (perm === 'view' && node in TAB_DEFAULT_VIEW) return (TAB_DEFAULT_VIEW[node] ?? []).includes(role)
  return undefined
}

/** Một bộ chấm quyền trên MỘT bảng phần chỉnh — tiến trình dùng một bộ (`can`), màn cấu hình dựng thêm bộ trên bản nháp. */
export interface GrantEvaluator {
  can: (role: string | undefined, node: string, perm?: Perm) => boolean
  /** Ô đang khai ở lớp nào: phần chỉnh thắng mặc định. `undefined` = như cha. */
  explicit: (role: string, node: string, perm: Perm) => boolean | undefined
  /** Ô này có khác mặc định không (danh sách "đang khác mặc định" trên màn cấu hình). */
  isOverridden: (role: string, node: string, perm: Perm) => boolean
  rows: () => GrantRow[]
}

export const makeGrantEvaluator = (rows: readonly GrantRow[]): GrantEvaluator => {
  const overrides = new Map(rows.map((r) => [keyOf(r.role, r.node, r.perm), r.allow]))
  const memo = new Map<string, boolean>()
  const explicit = (role: string, node: string, perm: Perm): boolean | undefined =>
    overrides.get(keyOf(role, node, perm)) ?? defaultGrant(role, node, perm)

  const evalGrant = (role: string, node: string, perm: Perm): boolean => {
    const n = uiNode(node)
    if (n === undefined || !n.perms.includes(perm)) return false
    if (role === 'admin') return true
    const vendor = inVendorTree(node)
    if (role === 'ncc' ? !vendor : vendor) return false
    if (perm !== 'view' && !check(role, node, 'view')) return false
    const p = n.parent === undefined ? undefined : uiNode(n.parent)
    // Cha không có quyền này (vd `/quan-tri` chỉ có Xem) ⇒ con bị chặn bởi quyền Xem của cha. Con không bao giờ vượt cha.
    const parentOk = p === undefined || check(role, p.id, p.perms.includes(perm) ? perm : 'view')
    const fixed = fixedRoles(node, perm)
    // Quyền về quyền: vẫn không vượt cha (GĐ mất Xem Danh mục thì mất cả màn Phân quyền — admin luôn còn).
    if (fixed !== undefined) return parentOk && fixed.includes(role)
    if (n.parent !== undefined) return parentOk && (explicit(role, node, perm) ?? true)
    return explicit(role, node, perm) ?? false
  }

  function check(role: string | undefined, node: string, perm: Perm = 'view'): boolean {
    if (!role) return false
    const k = keyOf(role, node, perm)
    const hit = memo.get(k)
    if (hit !== undefined) return hit
    const v = evalGrant(role, node, perm)
    memo.set(k, v)
    return v
  }

  return {
    can: check,
    explicit,
    isOverridden: (role, node, perm) => overrides.has(keyOf(role, node, perm)),
    rows: () => [...overrides.entries()].map(([k, allow]) => {
      const [role, node, perm] = k.split('|') as [string, string, Perm]
      return { role, node, perm, allow }
    }),
  }
}

let current = makeGrantEvaluator([])
let revision = 0

/** Nạp phần chỉnh (bảng `role_grant`) kèm số bản. Cùng số bản ⇒ không làm gì. */
/* ══ PHẠM VI DỮ LIỆU THEO VAI — kiểu KẾT HỢP (D03c §5, anh chốt 29/10) ══════════════════════════════════════════
   Vai có MẶC ĐỊNH; hồ sơ người ghi đè được ("Riêng: tất cả / của mình"), để trống = theo vai. Mặc định trong mã = hiện
   trạng (`azScopeOf`: sale ⇒ của mình; `PAY_REQUEST_OWN_ONLY_ROLES` ⇒ phiếu của mình); màn Phân quyền chỉnh được trừ ô khoá. */
export const SCOPE_DOMAINS = ['orders', 'payRequests'] as const
export type ScopeDomain = (typeof SCOPE_DOMAINS)[number]
export type Scope = 'own' | 'all'
export interface ScopeRow { role: string; domain: ScopeDomain; scope: Scope }

const scopeDefault = (role: string, domain: ScopeDomain): Scope =>
  domain === 'orders'
    ? (role === 'sale' ? 'own' : 'all')
    : ((PAY_REQUEST_OWN_ONLY_ROLES as readonly string[]).includes(role) ? 'own' : 'all')

/** Ô phạm vi khoá cứng: trả lý do, hoặc `null` nếu sửa được trên màn Phân quyền. */
export const scopeLockedReason = (role: string, domain: ScopeDomain): string | null => {
  if (role === 'admin') return 'Quản trị luôn thấy tất cả.'
  if (role === 'ncc') return 'Thầu phụ chỉ thấy việc của chính mình ở cổng thầu phụ.'
  if (domain === 'payRequests' && (PAY_REQUEST_OWN_ONLY_ROLES as readonly string[]).includes(role)) {
    return 'Trưởng phòng KD · Sale chỉ thấy phiếu chi của mình (anh chốt 24/10).'
  }
  return null
}

/** Phạm vi mặc định của vai trên một bảng chỉnh cho trước — màn cấu hình tính trên bản nháp. */
export const roleScopeWith = (rows: readonly ScopeRow[], role: string, domain: ScopeDomain): Scope => {
  if (scopeLockedReason(role, domain) !== null) return role === 'admin' ? 'all' : scopeDefault(role, domain)
  return rows.find((r) => r.role === role && r.domain === domain)?.scope ?? scopeDefault(role, domain)
}

/** Dòng chỉnh phạm vi có hợp lệ để lưu không. */
export const scopeRowError = (r: ScopeRow): string | null => {
  if (!(SCOPE_DOMAINS as readonly string[]).includes(r.domain)) return `Phạm vi "${r.domain}" không có.`
  if (!(STAFF_ROLES as readonly string[]).includes(r.role)) return `Vai "${r.role}" không có trong RT.`
  return scopeLockedReason(r.role, r.domain)
}

let scopeRows: readonly ScopeRow[] = []

/** Nạp phần chỉnh (bảng `role_grant` + `role_scope`) kèm số bản. Cùng số bản ⇒ không làm gì. */
export const setGrantOverrides = (rev: number, rows: readonly GrantRow[], scopes: readonly ScopeRow[] = []): void => {
  if (rev === revision && rows.length === current.rows().length && scopes.length === scopeRows.length) return
  current = makeGrantEvaluator(rows)
  scopeRows = [...scopes]
  revision = rev
}
export const grantScopeRows = (): ScopeRow[] => [...scopeRows]

/** Phạm vi MẶC ĐỊNH của vai đang áp (đã tính phần chỉnh). Hồ sơ người để trống phạm vi ⇒ dùng giá trị này. */
export const roleScope = (role: string, domain: ScopeDomain): Scope => roleScopeWith(scopeRows, role, domain)
export const grantRevision = (): number => revision
export const grantOverrideRows = (): GrantRow[] => current.rows()
export const explicitGrant = (role: string, node: string, perm: Perm): boolean | undefined => current.explicit(role, node, perm)
export const isOverridden = (role: string, node: string, perm: Perm): boolean => current.isOverridden(role, node, perm)

/**
 * ══ MỘT HÀM DUY NHẤT: vai này có quyền `perm` trên nút `node` không ══
 * Nút lạ ⇒ KHÔNG (từ chối mặc định — KIEN-TRUC §2.3). Vai rỗng ⇒ KHÔNG.
 */
export function can(role: string | undefined, node: string, perm: Perm = 'view'): boolean {
  return current.can(role, node, perm)
}

/** Mọi ô `node|perm` vai này đang có — để gửi xuống web và để Cedar đọc (Đợt 3). */
export const effectiveGrants = (role: string): string[] =>
  UI_TREE.flatMap((n) => PERMS.filter((p) => can(role, n.id, p)).map((p) => `${n.id}|${p}`))

/**
 * Một dòng phần chỉnh có HỢP LỆ để lưu không — trả lý do, hoặc `null`. Máy chủ gọi trước khi ghi (cửa lưu của màn
 * Phân quyền); màn gọi để không bày ô sẽ bị từ chối. Nút lạ / quyền nút không có / ô khoá cứng ⇒ từ chối.
 */
export const grantRowError = (r: GrantRow): string | null => {
  const n = uiNode(r.node)
  if (n === undefined) return `Không có mục "${r.node}" trong sổ giao diện.`
  if (!n.perms.includes(r.perm)) return `Mục "${r.node}" không có quyền "${r.perm}".`
  if (!(STAFF_ROLES as readonly string[]).includes(r.role) && r.role !== 'ncc') return `Vai "${r.role}" không có trong RT.`
  return lockedReason(r.role, r.node, r.perm)
}

const normPath = (pathname: string): string => (pathname || '/').split('?')[0]?.replace(/\/+$/, '') || '/'

/** Màn chứa đường dẫn này — khớp TIỀN TỐ (`/finance/abc` thuộc `/finance`), nhưng `/approvals` KHÔNG thuộc `/ap`. */
const screenOf = (p: string): { id: string; path: string } | undefined => {
  let best: { id: string; path: string } | undefined
  for (const n of UI_TREE) {
    if (n.kind !== 'screen' || n.path === undefined) continue
    if ((p === n.path || p.startsWith(`${n.path}/`)) && (best === undefined || n.path.length > best.path.length)) {
      best = { id: n.id, path: n.path }
    }
  }
  return best
}

/**
 * ══ ĐƯỜNG DẪN NÀY CÓ MỞ ĐƯỢC VỚI NGƯỜI NÀY KHÔNG — một luật cho menu, router web, plugin máy chủ và các hàm `may*` ══
 * Chuyển từ `routes.ts` (D03c Đợt 1): quyền Xem của MÀN giờ lấy từ `can()`; mặc định đúng luật vai cũ nên CSDL trống thì
 * kết quả y hệt (`matrix.test.ts` — bảng 232 ô gõ tay).
 *
 * FAIL-CLOSED: vai rỗng ⇒ `false`. Đường dẫn công khai ⇒ `true`. `ncc` ⇒ chỉ đường dẫn công khai (cổng `/ncc` nằm trong
 * đó) — đứng TRƯỚC mọi luật khác, có cờ giá vốn gì cũng không cộng thêm.
 *
 * `u`: cờ giá vốn CỦA TỪNG NGƯỜI. Màn giá vốn (`costViewEscalation`) — vai ngoài `roles` của luật cũ (TPKD, sale) phải có
 * thêm quyền xem giá vốn (tick hoặc phạm vi khác `own`, `canSeeVendorCost`). Đây là §4.4, KHÔNG phải ô tick; Đợt 4 tách.
 * Gọi thiếu `u` thì coi như không có cờ nào.
 */
export function canAccessRoute(role: string | undefined, pathname: string, u: CostViewFlags = {}): boolean {
  if (!role) return false
  const p = normPath(pathname)
  if (PUBLIC_ROUTES.some((x) => p === x || p.startsWith(`${x}/`))) return true
  if (role === 'ncc') return false
  const screen = screenOf(p)
  // Không thuộc màn nào (`/`, đường dẫn lạ) ⇒ như cũ: ai đăng nhập cũng qua; router tự đưa về trang chủ.
  if (screen === undefined) return true
  if (!can(role, screen.id, 'view')) return false
  const rule = RESTRICTED_ROUTES.find((r) => r.path === screen.path)
  if (rule?.costViewEscalation === true && !rule.roles.includes(role)) {
    return (AZ_OFFICE as readonly string[]).includes(role) && canSeeVendorCost(role, u)
  }
  return true
}

/**
 * Mục menu của đường dẫn này có VẼ cho người này không — `canAccessRoute` cộng `menuRoles` (nếu luật khai).
 * ⚠ Menu ⊆ đường dẫn LUÔN đúng. Màn ẩn khỏi menu (`hidden` trong sổ) không có lá ở `nav.ts` nên không cần xét ở đây.
 */
export function canShowInMenu(role: string | undefined, pathname: string, u: CostViewFlags = {}): boolean {
  if (!canAccessRoute(role, pathname, u)) return false
  const p = normPath(pathname)
  const rule = RESTRICTED_ROUTES.find((r) => p === r.path || p.startsWith(`${r.path}/`))
  return rule?.menuRoles === undefined || rule.menuRoles.includes(role ?? '')
}

/**
 * ══ "MỞ THÊM" QUYỀN XEM PHẢI MỞ ĐƯỢC CẢ CỬA ĐỌC (D03c §7.1) ══════════════════════════════════════════════
 * Cedar còn gác hành động ĐỌC bằng danh sách vai viết sẵn (`ar:read` = văn phòng…). Anh mở màn Phải thu cho Sale trên màn
 * Phân quyền mà Cedar vẫn từ chối `ar:read` thì màn mở ra trống. Bảng dưới khai mỗi hành động ĐỌC đang phục vụ những màn
 * nào (kiểm kê 28/10); `plugins/authorize.ts` cho qua một lệnh GET Cedar từ chối KHI VÀ CHỈ KHI vai đó được MỞ THÊM Xem
 * (phần chỉnh bật, mặc định tắt) ít nhất một màn trong danh sách.
 *   · CSDL trống ⇒ không bao giờ nới ⇒ y hệt hôm nay.
 *   · Chỉ ĐỌC. Ghi vẫn theo Cedar + luật module (nút Sửa/Thêm/Xuất làm từng màn).
 *   · Màn chưa khai ở đây ⇒ không nới (từ chối mặc định); lớp kiểm của module (`may*`, cắt giá vốn theo cờ người) vẫn chạy.
 *   · CỐ Ý KHÔNG có `audit:read` · `grants:read` (quyền về quyền) · `ncc:portal`.
 */
export const READ_ACTION_SCREENS: Readonly<Record<string, readonly string[]>> = {
  'finance:read': ['/finance', '/chi', '/az/dashboard'],
  'ledger:audit': ['/finance'],
  'ar:read': ['/ar', '/az/dashboard'],
  'billing:read': ['/az/billing', '/ar', '/az/dashboard'],
  'ap:read': ['/ap', '/az/purchase-docs', '/az/ops', '/az/dashboard'],
  'amis:read': ['/chung-tu', '/ar'],
  'order:read': ['/az/sales', '/az/ops', '/az/billing', '/az/vendor-pnl'],
  'ops:read': ['/az/ops', '/az/chao-gia', '/az/sales'],
  'cost:read': ['/az/sales', '/az/ops', '/az/stats', '/az/vendor-pnl', '/az/gia-duong-sat', '/az/ops-catalog', '/az/dashboard'],
  'hoahong:read': ['/az/hoa-hong', '/az/stats'],
  'hoahongCuaToi:read': ['/hoa-hong-cua-toi', '/az/stats'],
  'kehoach:read': ['/az/ke-hoach', '/az/dashboard'],
  'bctc:read': ['/az/dashboard'],
  'legacy:read': ['/az/sales'],
  'dntt:read': ['/approvals'],
  'price:read': ['/az/pricing', '/az/ops-catalog'],
  'quote:read': ['/az/pricing'],
  'custPolicy:read': ['/az/pricing'],
  'opsCatalog:read': ['/az/ops-catalog', '/az/gia-duong-sat'],
}

const DEFAULTS = makeGrantEvaluator([])

/** Vai này có được MỞ THÊM Xem một màn dùng hành động đọc `action` không (xem `READ_ACTION_SCREENS`). */
export const readWidenedFor = (role: string | undefined, action: string): boolean => {
  if (!role || role === 'admin' || role === 'ncc') return false
  for (const path of READ_ACTION_SCREENS[action] ?? []) {
    const s = screenOf(path)
    if (s !== undefined && can(role, s.id, 'view') && !DEFAULTS.can(role, s.id, 'view')) return true
  }
  return false
}

/**
 * ══ TẮT BỚT SỬA/THÊM ⇒ CHẶN GHI Ở MÁY CHỦ (D03c Đợt 5) ═════════════════════════════════════════════════════════
 * Lệnh ghi (không phải GET) của một cửa gác theo màn (`rtPath`/`screenPath`) đòi vai còn Sửa/Xoá HOẶC Thêm/Nhập ở màn đó.
 * Chỉ CHẶN THÊM, không bao giờ mở: mặc định Sửa/Thêm theo Xem nên CSDL trống thì y hệt hôm nay; Cedar và luật module vẫn
 * chấm như cũ. "Hoặc" chứ không tách theo phương thức: nhiều lệnh POST là thao tác trên bản ghi có sẵn (xác nhận, ghi sổ…),
 * tách sai là chặn oan.
 */
export const writeAllowedOn = (role: string | undefined, pathname: string): boolean => {
  if (!role) return false
  const s = screenOf(normPath(pathname))
  if (s === undefined) return true
  return can(role, s.id, 'edit') || can(role, s.id, 'create')
}

/** Màn (mục `screen` gần nhất phía trên, kể cả chính nó) chứa một mục sổ. */
const screenPathOfNode = (id: string): string | undefined => {
  const n = uiNode(id)
  if (n === undefined) return undefined
  if (n.kind === 'screen') return n.path
  for (const a of [...uiAncestors(id)].reverse()) if (a.kind === 'screen') return a.path
  return undefined
}

/**
 * ══ CỬA ĐỌC GÁC THEO MỤC SỔ (D03c §7.1 — "ẩn tab là cắt dữ liệu") ════════════════════════════════════════════
 * Route khai `ui: [mục…]` (màn hoặc tab dùng cửa đó). Người gọi phải còn Xem ÍT NHẤT MỘT mục — cộng điều kiện của
 * màn chứa mục (`canAccessRoute`: màn giá vốn đòi cờ giá vốn của người với TPKD · sale). Mục lạ ⇒ không tính.
 */
export const canViewNode = (role: string | undefined, id: string, u: CostViewFlags = {}): boolean => {
  if (!can(role, id, 'view')) return false
  const path = screenPathOfNode(id)
  return path === undefined || canAccessRoute(role, path, u)
}
export const canViewAny = (role: string | undefined, ids: readonly string[], u: CostViewFlags = {}): boolean =>
  ids.some((id) => canViewNode(role, id, u))

/** Vai được MỞ THÊM Xem (phần chỉnh bật, mặc định tắt) ít nhất một mục của cửa — nới cửa đọc như `readWidenedFor`. */
export const uiWidenedFor = (role: string | undefined, ids: readonly string[]): boolean => {
  if (!role || role === 'admin' || role === 'ncc') return false
  return ids.some((id) => can(role, id, 'view') && !DEFAULTS.can(role, id, 'view'))
}
